import { useCallback, useState } from 'react'

import { useFirebase } from '../providers/FirebaseProvider'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface AdminApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
}

interface AdminUser {
  id: string // email
  name: string
  picture: string
  status: 'active' | 'inactive'
  permissions: string[]
  created_at: string
  updated_at: string
  last_login_at?: string
}

export function useAdminApi() {
  const { user, refreshToken } = useFirebase()
  const [loading, setLoading] = useState(false)

  const makeApiCall = useCallback(
    async <T = any>(
      endpoint: string,
      options: RequestInit = {}
    ): Promise<AdminApiResponse<T>> => {
      if (!user?.accessToken) {
        return { success: false, error: 'No access token available' }
      }

      setLoading(true)
      try {
        const token = user.accessToken

        const makeRequest = async (authToken: string) => {
          const response = await fetch(`${API_BASE_URL}/api/admin${endpoint}`, {
            ...options,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
              ...options.headers,
            },
          })

          if (response.status === 401) {
            // Token might be expired, try to refresh
            const newToken = await refreshToken()
            if (newToken && newToken !== authToken) {
              // Retry with new token
              return makeRequest(newToken)
            }
            throw new Error('Unauthorized')
          }

          if (!response.ok) {
            const errorData = await response.json().catch(() => null)
            throw new Error(errorData?.error || `HTTP ${response.status}`)
          }

          return response.json()
        }

        const result = await makeRequest(token)
        return result
      } catch (error) {
        console.error('Admin API error:', error)
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      } finally {
        setLoading(false)
      }
    },
    [user?.accessToken, refreshToken]
  )

  const getProfile = useCallback(async (): Promise<
    AdminApiResponse<AdminUser>
  > => {
    return makeApiCall<AdminUser>(`/profile`)
  }, [makeApiCall])

  const listShops = useCallback(
    async (params: Record<string, any> = {}): Promise<AdminApiResponse> => {
      const queryString = new URLSearchParams(params).toString()
      const endpoint = queryString ? `/shops?${queryString}` : '/shops'
      return makeApiCall(endpoint)
    },
    [makeApiCall]
  )

  const getShopsStats = useCallback(async (): Promise<AdminApiResponse> => {
    return makeApiCall('/shops/stats')
  }, [makeApiCall])

  const listAdmins = useCallback(
    async (params: Record<string, any> = {}): Promise<AdminApiResponse> => {
      const queryString = new URLSearchParams(params).toString()
      const endpoint = queryString ? `/admins?${queryString}` : '/admins'
      return makeApiCall(endpoint)
    },
    [makeApiCall]
  )

  const updateAdminStatus = useCallback(
    async (
      adminId: string,
      status: 'active' | 'inactive'
    ): Promise<AdminApiResponse> => {
      return makeApiCall(`/admins/${adminId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      })
    },
    [makeApiCall]
  )

  const updateAdminPermissions = useCallback(
    async (
      adminId: string,
      permissions: string[]
    ): Promise<AdminApiResponse> => {
      return makeApiCall(`/admins/${adminId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions }),
      })
    },
    [makeApiCall]
  )

  const generateShopToken = useCallback(
    async (myshopifyDomain: string): Promise<AdminApiResponse> => {
      return makeApiCall(
        `/shops/${encodeURIComponent(myshopifyDomain)}/token`,
        {
          method: 'POST',
        }
      )
    },
    [makeApiCall]
  )

  const listTestStores = useCallback(async (): Promise<AdminApiResponse> => {
    return makeApiCall('/test-stores')
  }, [makeApiCall])

  const addTestStore = useCallback(
    async (
      myshopifyDomain: string,
      note: string
    ): Promise<AdminApiResponse> => {
      return makeApiCall('/test-stores', {
        method: 'POST',
        body: JSON.stringify({ myshopify_domain: myshopifyDomain, note }),
      })
    },
    [makeApiCall]
  )

  const removeTestStore = useCallback(
    async (myshopifyDomain: string): Promise<AdminApiResponse> => {
      return makeApiCall(
        `/test-stores/${encodeURIComponent(myshopifyDomain)}`,
        {
          method: 'DELETE',
        }
      )
    },
    [makeApiCall]
  )

  const listDiscounts = useCallback(async (): Promise<AdminApiResponse> => {
    return makeApiCall('/discounts')
  }, [makeApiCall])

  const createDiscount = useCallback(
    async (discount: Record<string, unknown>): Promise<AdminApiResponse> => {
      return makeApiCall('/discounts', {
        method: 'POST',
        body: JSON.stringify(discount),
      })
    },
    [makeApiCall]
  )

  const updateDiscount = useCallback(
    async (
      code: string,
      discount: Record<string, unknown>
    ): Promise<AdminApiResponse> => {
      return makeApiCall(`/discounts/${encodeURIComponent(code)}`, {
        method: 'PUT',
        body: JSON.stringify(discount),
      })
    },
    [makeApiCall]
  )

  const removeDiscount = useCallback(
    async (code: string): Promise<AdminApiResponse> => {
      return makeApiCall(`/discounts/${encodeURIComponent(code)}`, {
        method: 'DELETE',
      })
    },
    [makeApiCall]
  )

  return {
    loading,
    GetProfile: getProfile,
    listShops,
    getShopsStats,
    listAdmins,
    updateAdminStatus,
    updateAdminPermissions,
    generateShopToken,
    listTestStores,
    addTestStore,
    removeTestStore,
    listDiscounts,
    createDiscount,
    updateDiscount,
    removeDiscount,
  }
}
